import { useTranslation } from 'react-i18next';
import type { PhotoKind } from '@nargus/shared';
import { Check, Film, ImageIcon, Star } from 'lucide-react';
import { useBlobUrl } from '@/hooks/use-blob-url';
import { useInView } from '@/hooks/use-in-view';
import { formatDuration } from '@/lib/media';
import { cn } from '@/lib/utils';

const MONTH = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });

export interface GridPhoto {
  id: string;
  filename: string;
  contentType: string;
  kind: PhotoKind;
  caption?: string;
  takenAt: string;
  hasThumbnail: boolean;
  durationMs?: number;
  favorite?: boolean;
}

interface MonthGroup<T> {
  key: string;
  label: string;
  photos: T[];
}

function groupByMonth<T extends GridPhoto>(photos: T[]): MonthGroup<T>[] {
  const groups: MonthGroup<T>[] = [];

  for (const photo of photos) {
    const date = new Date(photo.takenAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const last = groups.at(-1);

    if (last?.key === key) last.photos.push(photo);
    else groups.push({ key, label: MONTH.format(date), photos: [photo] });
  }

  return groups;
}

export function PhotoGrid<T extends GridPhoto>({
  photos,
  loadThumbnail,
  selected,
  onSelect,
  onOpen,
  grouped = true,
}: {
  photos: T[];
  loadThumbnail: (photo: T) => Promise<Blob>;
  selected?: Set<string>;
  onSelect?: (photo: T) => void;
  onOpen: (photo: T) => void;
  grouped?: boolean;
}) {
  const groups = grouped
    ? groupByMonth(photos)
    : [{ key: 'all', label: '', photos }];

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.key} className="flex flex-col gap-2">
          {group.label && (
            <h3 className="sticky top-14 z-1 bg-background/90 py-1 text-sm font-medium backdrop-blur">
              {group.label}
            </h3>
          )}
          <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
            {group.photos.map((photo) => (
              <Tile
                key={photo.id}
                photo={photo}
                loadThumbnail={loadThumbnail}
                selecting={(selected?.size ?? 0) > 0}
                selected={selected?.has(photo.id) ?? false}
                onSelect={onSelect}
                onOpen={onOpen}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Tile<T extends GridPhoto>({
  photo,
  loadThumbnail,
  selecting,
  selected,
  onSelect,
  onOpen,
}: {
  photo: T;
  loadThumbnail: (photo: T) => Promise<Blob>;
  selecting: boolean;
  selected: boolean;
  onSelect?: (photo: T) => void;
  onOpen: (photo: T) => void;
}) {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLLIElement>();
  const { url } = useBlobUrl(inView && photo.hasThumbnail ? photo.id : '', () =>
    loadThumbnail(photo),
  );

  const label = photo.caption ?? photo.filename;
  const Fallback = photo.kind === 'video' ? Film : ImageIcon;

  return (
    <li ref={ref} className="group relative">
      <button
        type="button"
        className={cn(
          'flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-muted/60 transition-all',
          selected ? 'ring-2 ring-primary ring-offset-2' : 'hover:opacity-90',
        )}
        aria-label={t('files.open', { filename: label })}
        onClick={() => (selecting && onSelect ? onSelect(photo) : onOpen(photo))}
      >
        {url ? (
          <img
            src={url}
            alt={label}
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <Fallback className="size-7 text-muted-foreground" strokeWidth={1.5} />
        )}
      </button>

      {photo.kind === 'video' && photo.durationMs !== undefined && (
        <span className="pointer-events-none absolute bottom-1 right-1 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white">
          <Film className="size-3" />
          {formatDuration(photo.durationMs)}
        </span>
      )}

      {photo.favorite && !selected && (
        <Star className="pointer-events-none absolute bottom-1 left-1 size-4 fill-white text-white drop-shadow" />
      )}

      {onSelect && (
        <button
          type="button"
          aria-label={
            selected
              ? t('photos.deselect', { filename: label })
              : t('photos.select', { filename: label })
          }
          aria-pressed={selected}
          className={cn(
            'absolute top-1 left-1 flex size-5 items-center justify-center rounded-full border transition-opacity',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-white/80 bg-black/30 text-transparent opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
          )}
          onClick={() => onSelect(photo)}
        >
          <Check className="size-3.5" strokeWidth={3} />
        </button>
      )}
    </li>
  );
}
